import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList, Plus, Trash2, Calendar, History,
  CheckCircle, XCircle, Shield, Eye, Clock, Pencil
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../components/ui/context-menu";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const QUOTA_MINUTES = 150;

const parseTimeToMinutes = (str) => {
  if (!str || !str.trim()) return 0;
  str = str.trim().toLowerCase();
  let hours = 0, minutes = 0;
  if (str.includes(":")) {
    const parts = str.split(":");
    return (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1]) || 0);
  }
  const hourMatch = str.match(/(\d+)\s*(h|hr|hrs|hour|hours)/);
  const minMatch = str.match(/(\d+)\s*(m|min|mins|minute|minutes)/);
  if (hourMatch) hours = parseInt(hourMatch[1]);
  if (minMatch) minutes = parseInt(minMatch[1]);
  if (!hourMatch && !minMatch && /^\d+$/.test(str)) return parseInt(str);
  return hours * 60 + minutes;
};

const formatMinutesToDisplay = (totalMinutes) => {
  if (totalMinutes <= 0) return "0m";
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const QuotaHistory = ({ currentUser, currentDiscordId, onViewStaffProfile }) => {
  const [quotaWeeks, setQuotaWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTime, setEditingTime] = useState(null);

  const fetchQuotaHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quota-history`);
      setQuotaWeeks(res.data);
      setSelectedWeek((prev) => {
        if (res.data.length > 0 && !prev) return res.data[0];
        if (prev) {
          const updated = res.data.find((w) => w.id === prev.id);
          return updated || prev;
        }
        return prev;
      });
    } catch {
      toast.error("Failed to load quota history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuotaHistory(); }, [fetchQuotaHistory]);

  const getWeekDates = (offset = 0) => {
    // Compute Monday→Sunday in America/New_York timezone.
    // Steps: get "now" in EST → find the Monday of that week (Mon=0..Sun=6)
    // → shift by offset weeks → derive Sunday.
    const nowEst = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const jsDay = nowEst.getDay(); // Sun=0..Sat=6
    // Convert to Mon=0..Sun=6
    const monIdx = (jsDay + 6) % 7;
    const monday = new Date(nowEst);
    monday.setDate(nowEst.getDate() - monIdx + offset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const isoDate = (d) => {
      // Local-YYYY-MM-DD (no toISOString → avoid UTC drift)
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return {
      weekStart: isoDate(monday), // stored as Monday ISO date going forward
      weekLabel: `${fmt(monday)} - ${fmt(sunday)}, ${monday.getFullYear()}`,
    };
  };

  const handleCreateWeek = async (weekOffset = 0) => {
    const { weekStart, weekLabel } = getWeekDates(weekOffset);
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/quota-history?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}`,
        { week_start: weekStart, week_label: weekLabel, created_by: currentUser }
      );
      toast.success("Quota week created!");
      setShowCreateModal(false);
      await fetchQuotaHistory();
      setSelectedWeek(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create quota week");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCompletion = async (staffId, currentStatus) => {
    if (!selectedWeek) return;
    try {
      await axios.put(
        `${API}/quota-history/${selectedWeek.id}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}`,
        [{ staff_id: staffId, completed: !currentStatus }]
      );
      const updateEntry = (entries) => entries.map((e) => (e.staff_id === staffId ? { ...e, completed: !currentStatus } : e));
      setSelectedWeek((prev) => ({ ...prev, entries: updateEntry(prev.entries) }));
      setQuotaWeeks((prev) => prev.map((w) => (w.id === selectedWeek.id ? { ...w, entries: updateEntry(w.entries) } : w)));
    } catch {
      toast.error("Failed to update quota");
    }
  };

  const handleToggleExempt = async (staffId, currentExempt) => {
    if (!selectedWeek) return;
    try {
      await axios.put(
        `${API}/quota-history/${selectedWeek.id}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}`,
        [{ staff_id: staffId, exempt: !currentExempt }]
      );
      const updateEntry = (entries) => entries.map((e) => (e.staff_id === staffId ? { ...e, exempt: !currentExempt } : e));
      setSelectedWeek((prev) => ({ ...prev, entries: updateEntry(prev.entries) }));
      setQuotaWeeks((prev) => prev.map((w) => (w.id === selectedWeek.id ? { ...w, entries: updateEntry(w.entries) } : w)));
    } catch {
      toast.error("Failed to update exempt status");
    }
  };

  const handleSaveTime = async (staffId) => {
    if (!selectedWeek || !editingTime) return;
    try {
      await axios.put(
        `${API}/quota-history/${selectedWeek.id}/entry/${staffId}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}`,
        { previous_time: editingTime.value }
      );
      const updateEntry = (entries) => entries.map((e) => (e.staff_id === staffId ? { ...e, previous_time: editingTime.value } : e));
      setSelectedWeek((prev) => ({ ...prev, entries: updateEntry(prev.entries) }));
      setQuotaWeeks((prev) => prev.map((w) => (w.id === selectedWeek.id ? { ...w, entries: updateEntry(w.entries) } : w)));
      setEditingTime(null);
    } catch {
      toast.error("Failed to update time");
    }
  };

  const handleDeleteWeek = async (weekId) => {
    if (!window.confirm("Delete this quota week?")) return;
    try {
      await axios.delete(`${API}/quota-history/${weekId}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}`);
      toast.success("Quota week deleted");
      setQuotaWeeks((prev) => prev.filter((w) => w.id !== weekId));
      if (selectedWeek?.id === weekId) {
        const remaining = quotaWeeks.filter((w) => w.id !== weekId);
        setSelectedWeek(remaining[0] || null);
      }
    } catch {
      toast.error("Failed to delete quota week");
    }
  };

  const handleEditWeekDate = async (week) => {
    // Prefill with the CURRENT Monday derived from the week
    const currentMonday = week.display_monday_iso || week.week_start || "";
    const input = window.prompt(
      `Change the Monday of "${week.display_week_label || week.week_label}".\n\nEnter the new Monday date (YYYY-MM-DD):`,
      currentMonday,
    );
    if (!input || input === currentMonday) return;
    const trimmed = input.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      toast.error("Invalid format. Use YYYY-MM-DD (e.g. 2026-05-11)");
      return;
    }
    try {
      const res = await axios.patch(
        `${API}/quota-history/${week.id}/dates?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}`,
        { week_start: trimmed },
      );
      toast.success(`Updated to ${res.data.display_week_label || res.data.week_label}`);
      setQuotaWeeks((prev) =>
        prev
          .map((w) => (w.id === week.id ? res.data : w))
          .sort((a, b) => (b.week_start || "").localeCompare(a.week_start || "")),
      );
      if (selectedWeek?.id === week.id) setSelectedWeek(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dates");
    }
  };

  const getCompletionStats = (entries) => {
    if (!entries || entries.length === 0) return { completed: 0, exempt: 0, total: 0, active: 0, percentage: 0 };
    const exempt = entries.filter((e) => e.exempt).length;
    const completed = entries.filter((e) => e.completed && !e.exempt).length;
    const active = entries.length - exempt;
    return { completed, exempt, total: entries.length, active, percentage: active > 0 ? Math.round((completed / active) * 100) : 0 };
  };

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="quota-history-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center border border-[#00E5FF]/30 bg-[#00E5FF]/10">
            <ClipboardList className="w-4 h-4 text-[#00E5FF]" />
          </div>
          <div>
            <h2 className="font-mono text-sm font-bold text-zinc-200 uppercase tracking-wider">Quota History</h2>
            <p className="font-mono text-[9px] text-zinc-600">Track weekly completions</p>
          </div>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2" data-testid="create-quota-week-btn">
          <Plus className="w-3.5 h-3.5" /> NEW WEEK
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Week Selector */}
        <div className="lg:col-span-1">
          <div className="surface-card overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h3 className="font-mono text-xs font-bold text-zinc-300 uppercase tracking-wider">Weeks</h3>
            </div>
            <div className="max-h-[500px] overflow-y-auto scrollbar-thin">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-5 h-5 border-2 border-[#00E5FF]/30 border-t-[#00E5FF] rounded-full animate-spin mx-auto" />
                </div>
              ) : quotaWeeks.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-zinc-600 font-mono text-xs">NO WEEKS YET</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800/50">
                  {quotaWeeks.map((week) => {
                    const stats = getCompletionStats(week.entries);
                    const isSelected = selectedWeek?.id === week.id;
                    return (
                      <div
                        key={week.id}
                        onClick={() => setSelectedWeek(week)}
                        className={`p-3 cursor-pointer transition-colors ${
                          isSelected ? "bg-[#00E5FF]/5 border-l-2 border-[#00E5FF]" : "hover:bg-zinc-800/30 border-l-2 border-transparent"
                        }`}
                        data-testid={`week-${week.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-zinc-300 font-mono text-[11px] font-medium">{week.display_week_label || week.week_label}</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditWeekDate(week); }}
                              className="p-0.5 hover:bg-zinc-700 opacity-40 hover:opacity-100 transition-opacity"
                              data-testid={`edit-week-${week.id}`}
                              title="Edit dates"
                            >
                              <Pencil className="w-3 h-3 text-[#00E5FF]" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteWeek(week.id); }}
                              className="p-0.5 hover:bg-zinc-700 opacity-40 hover:opacity-100 transition-opacity"
                              data-testid={`delete-week-${week.id}`}
                              title="Delete week"
                            >
                              <Trash2 className="w-3 h-3 text-[#FF3366]" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-zinc-800 overflow-hidden">
                            <div className="h-full bg-[#00E5FF] transition-all duration-300" style={{ width: `${stats.percentage}%` }} />
                          </div>
                          <span className="text-[10px] text-zinc-500 font-mono">{stats.completed}/{stats.active}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quota Entries Table */}
        <div className="lg:col-span-3">
          <div className="surface-card overflow-hidden">
            {selectedWeek ? (
              <>
                <div className="px-4 py-3 border-b border-zinc-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-mono text-xs font-bold text-zinc-200 uppercase tracking-wider">{selectedWeek.display_week_label || selectedWeek.week_label}</h3>
                      <p className="font-mono text-[9px] text-zinc-600 mt-0.5">
                        {getCompletionStats(selectedWeek.entries).completed} of {getCompletionStats(selectedWeek.entries).active} completed
                        {getCompletionStats(selectedWeek.entries).exempt > 0 && ` | ${getCompletionStats(selectedWeek.entries).exempt} exempt`}
                      </p>
                    </div>
                    <span className={`font-mono text-xl font-bold ${
                      getCompletionStats(selectedWeek.entries).percentage >= 80 ? "text-emerald-400" :
                      getCompletionStats(selectedWeek.entries).percentage >= 50 ? "text-amber-400" : "text-[#FF3366]"
                    }`}>
                      {getCompletionStats(selectedWeek.entries).percentage}%
                    </span>
                  </div>
                </div>
                <div className="max-h-[500px] overflow-y-auto scrollbar-thin">
                  <table className="w-full">
                    <thead className="sticky top-0">
                      <tr className="table-header">
                        <th className="text-left py-2.5 px-4">Staff Name</th>
                        <th className="text-center py-2.5 px-4">Time</th>
                        <th className="text-center py-2.5 px-4">Reports</th>
                        <th className="text-center py-2.5 px-4">Exempt</th>
                        <th className="text-center py-2.5 px-4">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedWeek.entries.map((entry) => (
                        <ContextMenu key={entry.staff_id}>
                          <ContextMenuTrigger asChild>
                            <tr className={`table-row cursor-pointer ${entry.exempt ? "opacity-40" : ""}`} data-testid={`quota-entry-${entry.staff_id}`}>
                          <td className="py-2.5 px-4 text-zinc-300 text-sm font-medium">
                            {entry.staff_name}
                            {entry.exempt && <span className="ml-2 text-[9px] font-mono text-amber-400">(EXEMPT)</span>}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            {editingTime?.staffId === entry.staff_id ? (
                              <div className="flex items-center gap-1 justify-center">
                                <input
                                  type="text" value={editingTime.value}
                                  onChange={(e) => setEditingTime({ ...editingTime, value: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === "Enter") handleSaveTime(entry.staff_id); if (e.key === "Escape") setEditingTime(null); }}
                                  className="input-field w-20 text-center text-xs py-1" autoFocus
                                  data-testid={`edit-time-input-${entry.staff_id}`}
                                />
                                <button onClick={() => handleSaveTime(entry.staff_id)} className="p-0.5 hover:bg-emerald-500/20" data-testid={`save-time-${entry.staff_id}`}>
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                                </button>
                                <button onClick={() => setEditingTime(null)} className="p-0.5 hover:bg-zinc-700">
                                  <XCircle className="w-3.5 h-3.5 text-zinc-500" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingTime({ staffId: entry.staff_id, value: entry.previous_time || "" })}
                                className={`font-mono text-xs cursor-pointer hover:text-white transition-colors px-1.5 py-0.5 hover:bg-zinc-700 ${entry.previous_time ? "text-[#00E5FF]" : "text-zinc-600"}`}
                                data-testid={`time-btn-${entry.staff_id}`}
                              >
                                {entry.previous_time || "-"}
                              </button>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <span className="font-mono text-xs text-purple-400" data-testid={`quota-game-reports-${entry.staff_id}`}>{entry.game_reports || 0}</span>
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <button
                              onClick={() => handleToggleExempt(entry.staff_id, entry.exempt)}
                              className={`w-7 h-7 flex items-center justify-center border transition-colors mx-auto ${
                                entry.exempt ? "bg-amber-500/15 border-amber-500/40 hover:bg-amber-500/25" : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                              }`}
                              data-testid={`toggle-exempt-${entry.staff_id}`}
                            >
                              {entry.exempt ? <Shield className="w-3.5 h-3.5 text-amber-400" /> : <span className="text-zinc-600 text-xs">-</span>}
                            </button>
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <button
                              onClick={() => !entry.exempt && handleToggleCompletion(entry.staff_id, entry.completed)}
                              disabled={entry.exempt}
                              className={`w-7 h-7 flex items-center justify-center border transition-colors mx-auto ${
                                entry.exempt ? "bg-zinc-800/50 border-zinc-700/50 cursor-not-allowed"
                                : entry.completed ? "bg-emerald-500/15 border-emerald-500/40 hover:bg-emerald-500/25"
                                : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                              }`}
                              data-testid={`toggle-quota-${entry.staff_id}`}
                            >
                              {entry.completed && !entry.exempt ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-zinc-600" />}
                            </button>
                          </td>
                        </tr>
                          </ContextMenuTrigger>
                          <ContextMenuContent className="bg-[#121212] border-zinc-800 rounded-none min-w-[160px]">
                            {onViewStaffProfile && (
                              <ContextMenuItem
                                onClick={() => onViewStaffProfile(entry.staff_id, entry.staff_name)}
                                className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                                data-testid={`ctx-view-quota-profile-${entry.staff_id}`}
                              >
                                <Eye className="w-3.5 h-3.5 mr-2 text-[#00E5FF]" />
                                VIEW PROFILE
                              </ContextMenuItem>
                            )}
                            <ContextMenuItem
                              onClick={() => setEditingTime({ staffId: entry.staff_id, value: entry.previous_time || "" })}
                              className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                              data-testid={`ctx-edit-time-${entry.staff_id}`}
                            >
                              <Clock className="w-3.5 h-3.5 mr-2 text-blue-400" />
                              EDIT TIME
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="p-16 text-center">
                <p className="text-zinc-600 font-mono text-xs">SELECT A WEEK TO VIEW</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Week Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 15 }}
              className="glass-overlay w-full max-w-sm p-5 relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="create-week-modal"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/50 to-transparent" />
              <div className="text-center mb-5">
                <div className="mx-auto w-10 h-10 border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex items-center justify-center mb-3">
                  <Calendar className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <h3 className="font-mono text-xs font-bold text-zinc-200 uppercase tracking-wider">Create Quota Week</h3>
              </div>

              <div className="space-y-2">
                {[
                  { offset: 0, label: "Current Week", sub: getWeekDates(0).weekLabel, icon: Calendar, accent: true },
                  { offset: -1, label: "Last Week", sub: getWeekDates(-1).weekLabel, icon: History },
                  { offset: -2, label: "Two Weeks Ago", sub: getWeekDates(-2).weekLabel, icon: History },
                ].map(({ offset, label, sub, icon: Icon, accent }) => (
                  <button
                    key={offset}
                    onClick={() => handleCreateWeek(offset)}
                    disabled={saving}
                    className={`w-full p-3 border text-left transition-colors ${
                      accent ? "border-[#00E5FF]/30 bg-[#00E5FF]/5 hover:bg-[#00E5FF]/10" : "border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800"
                    }`}
                    data-testid={`create-week-offset-${offset}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-zinc-200 text-xs font-mono font-medium">{label}</p>
                        <p className={`text-[10px] font-mono ${accent ? "text-[#00E5FF]" : "text-zinc-500"}`}>{sub}</p>
                      </div>
                      <Icon className={`w-4 h-4 ${accent ? "text-[#00E5FF]" : "text-zinc-600"}`} />
                    </div>
                  </button>
                ))}
              </div>

              <button onClick={() => setShowCreateModal(false)} className="w-full mt-3 btn-secondary">CANCEL</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QuotaHistory;
